import React, { useState } from 'react'
import moment from 'moment'

import AddIcon from '@mui/icons-material/Add'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import HowToRegIcon from '@mui/icons-material/HowToReg'
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import RosterLegend from './RosterLegend'
import {
  describeAvailableStaff,
  getShiftCardStyle,
  getShiftItemLabel,
  getShiftsForDay,
  getVisibleRosterStatusLegend,
  isOnCallShift,
} from './globalRosterUtils'
import useIsMobile from '../../../../helpers/useIsMobile'

const MAX_VISIBLE_ITEMS = 4

// Phones: narrower day columns, the calendar scrolls sideways inside the
// Paper like the week grid does
const columnMinWidth = (compact) => (compact ? 104 : 128)

const todayHighlightSx = {
  bgcolor: 'rgba(255, 193, 7, 0.14)',
  boxShadow: 'inset 0 0 0 2px rgba(245, 124, 0, 0.34)',
}

// A cell only ever shows the part of a shift that falls on its day, so the
// time is the edge that happens today: "20:00 →" when it runs on overnight,
// "← 08:00" when it started yesterday, "←→" when it covers the whole day.
const getDayTimeLabel = (shift, day) => {
  const startsBefore = moment(shift.start).isBefore(day.clone().startOf('day'))
  const endsAfter = moment(shift.end).isAfter(day.clone().endOf('day'))

  if (startsBefore && endsAfter) return '←→'
  if (startsBefore) return `← ${moment(shift.end).format('HH:mm')}`
  if (endsAfter) return `${moment(shift.start).format('HH:mm')} →`
  return moment(shift.start).format('HH:mm')
}

const ShiftItem = ({ shift, day, displayMode, pendingApplications, onClick }) => {
  const shiftStyle = getShiftCardStyle(shift, pendingApplications)
  const onCall = isOnCallShift(shift)
  const label = getShiftItemLabel(shift, displayMode)
  const fullRange = `${moment(shift.start).format('ddd D MMM HH:mm')} – ${moment(
    shift.end,
  ).format('ddd D MMM HH:mm')}`

  return (
    <Tooltip title={`${fullRange} · ${label}`} enterDelay={600}>
      <Box
        onClick={(e) => {
          e.stopPropagation()
          onClick()
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 0.6,
          py: 0.2,
          borderRadius: 0.75,
          bgcolor: shiftStyle.bg,
          color: shiftStyle.fg,
          border: onCall ? '1.5px dashed' : '1px solid',
          borderColor: shiftStyle.border,
          cursor: 'pointer',
          fontSize: '0.7rem',
          lineHeight: 1.4,
          minWidth: 0,
          '&:hover': { filter: 'brightness(0.96)', boxShadow: 1 },
        }}
      >
        {onCall && (
          <PhoneInTalkIcon sx={{ fontSize: 12, color: 'inherit', flexShrink: 0 }} />
        )}
        <Box component="span" sx={{ fontWeight: 800, flexShrink: 0 }}>
          {getDayTimeLabel(shift, day)}
        </Box>
        <Box
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Box>
        {shift.duplicateCount > 1 && (
          <Box
            component="span"
            sx={{
              px: 0.5,
              borderRadius: 0.75,
              bgcolor: shiftStyle.fg,
              color: shiftStyle.bg,
              fontWeight: 800,
              flexShrink: 0,
            }}
          >
            ×{shift.duplicateCount}
          </Box>
        )}
        {pendingApplications.length > 0 && (
          <HowToRegIcon sx={{ fontSize: 13, color: 'inherit', flexShrink: 0 }} />
        )}
      </Box>
    </Tooltip>
  )
}

const RosterMonthCalendar = ({
  monthStart,
  shifts,
  displayMode,
  selectedRoster,
  getShiftApplications,
  openExistingShift,
  openNewShift,
  onOpenWeek,
  // YYYY-MM-DD → { count, names } of staff who have said they can work
  availableStaffByDay = {},
}) => {
  const compact = useIsMobile('sm')
  const [moreAnchor, setMoreAnchor] = useState(null)
  const todayKey = moment().format('YYYY-MM-DD')

  const visibleLegend = getVisibleRosterStatusLegend(
    [{ shifts }],
    getShiftApplications,
  )

  // Full weeks so the grid is always 7 wide; days outside the month are
  // shown muted and empty (their shifts aren't loaded for this range)
  const gridStart = monthStart.clone().startOf('month').startOf('week')
  const gridEnd = monthStart.clone().endOf('month').endOf('week')
  const weeks = []
  for (
    const cursor = gridStart.clone();
    cursor.isSameOrBefore(gridEnd, 'day');
    cursor.add(1, 'week')
  ) {
    weeks.push(
      Array.from({ length: 7 }, (_, i) => cursor.clone().add(i, 'days')),
    )
  }
  const weekdayLabels = weeks[0].map((day) => day.format(compact ? 'dd' : 'ddd'))

  const gridSx = {
    display: 'grid',
    gridTemplateColumns: `repeat(7, minmax(${columnMinWidth(compact)}px, 1fr))`,
    minWidth: `calc(7 * ${columnMinWidth(compact)}px)`,
  }

  const renderItems = (dayShifts, day) =>
    dayShifts.map((shift) => (
      <ShiftItem
        key={shift._id}
        shift={shift}
        day={day}
        displayMode={displayMode}
        pendingApplications={getShiftApplications(shift)}
        onClick={() => {
          setMoreAnchor(null)
          openExistingShift(shift)
        }}
      />
    ))

  return (
    <Paper
      variant="outlined"
      sx={{ overflow: 'auto', maxHeight: '72vh', borderRadius: 2 }}
    >
      <RosterLegend items={visibleLegend} sx={{ minWidth: gridSx.minWidth }} />

      <Box
        sx={{
          ...gridSx,
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          zIndex: 3,
        }}
      >
        {weekdayLabels.map((label) => (
          <Box key={label} sx={{ p: 1, textAlign: 'center' }}>
            <Typography variant="subtitle2" fontWeight={800}>
              {label}
            </Typography>
          </Box>
        ))}
      </Box>

      {shifts.length === 0 && (
        <Alert
          severity="info"
          sx={{ m: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              disabled={!selectedRoster}
              onClick={() => openNewShift(monthStart)}
            >
              Add Shift
            </Button>
          }
        >
          {selectedRoster
            ? 'No shifts found for this month.'
            : 'No shifts found for this month. To get started, create a rota plan with the New button in the control panel above — you can then add shifts to it.'}
        </Alert>
      )}

      {weeks.map((week) => (
        <Box
          key={week[0].format('YYYY-MM-DD')}
          sx={{
            ...gridSx,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '&:last-of-type': { borderBottom: 0 },
          }}
        >
          {week.map((day) => {
            const key = day.format('YYYY-MM-DD')
            const inMonth = day.isSame(monthStart, 'month')
            const isToday = key === todayKey
            const dayShifts = inMonth ? getShiftsForDay(shifts, day) : []
            const hidden = Math.max(dayShifts.length - MAX_VISIBLE_ITEMS, 0)
            const uncovered = dayShifts.filter(
              (shift) =>
                !shift.assignedTo &&
                !shift.assignedSubcontractor &&
                !shift.assignedToSubcontractor,
            ).length
            const available = inMonth ? availableStaffByDay[key] : null

            return (
              <Box
                key={key}
                sx={{
                  p: 0.75,
                  minHeight: compact ? 96 : 124,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  '&:last-of-type': { borderRight: 0 },
                  bgcolor: inMonth ? 'background.paper' : 'grey.50',
                  ...(isToday ? todayHighlightSx : {}),
                  '&:hover .roster-month-add': { opacity: 1 },
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 0.5, minHeight: 26 }}
                >
                  <Tooltip title={inMonth ? 'Open this week' : ''}>
                    <Box
                      onClick={inMonth ? () => onOpenWeek(day) : undefined}
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: inMonth ? 'pointer' : 'default',
                        fontWeight: 800,
                        fontSize: '0.8rem',
                        color: inMonth ? 'text.primary' : 'text.disabled',
                        bgcolor: isToday ? 'warning.main' : 'transparent',
                        '&:hover': inMonth
                          ? { bgcolor: isToday ? 'warning.dark' : 'action.hover' }
                          : {},
                      }}
                    >
                      {day.format('D')}
                    </Box>
                  </Tooltip>
                  {inMonth && (
                    <Stack direction="row" alignItems="center" gap={0.25}>
                      {dayShifts.length > 0 && (
                        <Tooltip
                          title={`${dayShifts.length} shift${
                            dayShifts.length === 1 ? '' : 's'
                          }${uncovered ? `, ${uncovered} uncovered` : ''}`}
                        >
                          <Typography
                            variant="caption"
                            fontWeight={800}
                            color={uncovered ? 'error.main' : 'text.secondary'}
                          >
                            {dayShifts.length}
                          </Typography>
                        </Tooltip>
                      )}
                      {selectedRoster && (
                        <Tooltip title="Add shift on this day">
                          <IconButton
                            className="roster-month-add"
                            size="small"
                            onClick={() => openNewShift(day)}
                            sx={{
                              p: 0.25,
                              opacity: compact ? 1 : 0,
                              transition: 'opacity .15s ease',
                            }}
                          >
                            <AddIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  )}
                </Stack>

                <Stack gap={0.4}>
                  {renderItems(dayShifts.slice(0, MAX_VISIBLE_ITEMS), day)}
                  {hidden > 0 && (
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="primary.main"
                      onClick={(e) =>
                        setMoreAnchor({ el: e.currentTarget, day, dayShifts })
                      }
                      sx={{
                        cursor: 'pointer',
                        px: 0.6,
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      +{hidden} more
                    </Typography>
                  )}
                </Stack>

                {available?.count > 0 && (
                  <Tooltip title={describeAvailableStaff(available)}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      gap={0.5}
                      sx={{
                        mt: 'auto',
                        pt: 0.5,
                        color: 'success.main',
                        cursor: 'default',
                      }}
                    >
                      <EventAvailableIcon sx={{ fontSize: 14 }} />
                      <Typography variant="caption" fontWeight={700} noWrap>
                        {available.count} staff available
                      </Typography>
                    </Stack>
                  </Tooltip>
                )}
              </Box>
            )
          })}
        </Box>
      ))}

      <Popover
        open={!!moreAnchor}
        anchorEl={moreAnchor?.el}
        onClose={() => setMoreAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 1.5, width: 300, maxHeight: 420 } } }}
      >
        {moreAnchor && (
          <Stack gap={0.5}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>
              {moreAnchor.day.format('dddd D MMMM')} ·{' '}
              {moreAnchor.dayShifts.length} shifts
            </Typography>
            {renderItems(moreAnchor.dayShifts, moreAnchor.day)}
          </Stack>
        )}
      </Popover>
    </Paper>
  )
}

export default RosterMonthCalendar
